import { withSSROptimizely } from '@zoopla/optimizely/server';
import { GetServerSideProps } from 'next';
import {
    GetAgentsDocument,
    GetAgentsQuery
} from './queries/getAgents.query.generated';
import { fetchGraphql } from '@zoopla/fetch-graphql';
import { BreadcrumbsProps } from '@zoopla-eng/zds-components/breadcrumbs';
import { AgentsSearchConnection } from './graphql.generated';
import { Taxonomy, TaxonomyPage, AgentsEcommerce } from '@zoopla/tracking';
import { ParsedUrlQuery } from 'querystring';

export const SEARCH_TYPE = {
    ALL: 'all',
    ESTATE_AGENTS: 'estate-agents',
    LETTING_AGENTS: 'letting-agents'
} as const;

export type SearchType = (typeof SEARCH_TYPE)[keyof typeof SEARCH_TYPE];

export type SuccessProps = {
    type: 'success';
    searchType: SearchType;
    initialLocation: string;
    companyName: string | null;
    taxonomy: Taxonomy;
    ecommerce: AgentsEcommerce;
    data: {
        agents: AgentsSearchConnection;
        breadcrumbs: BreadcrumbsProps['items'];
        isLoggedIn: boolean;
    };
};
export type ErrorProps = {
    type: 'error';
    initialLocation: string;
    error: string;
    taxonomy: Taxonomy;
    ecommerce?: AgentsEcommerce;
};
export type PageProps = SuccessProps | ErrorProps;

export function apiToZdsBreadcrumbs(
    breadcrumbs: GetAgentsQuery['breadcrumbs']
): BreadcrumbsProps['items'] {
    if (!breadcrumbs) return [];

    return breadcrumbs.map((b, i) => {
        return {
            ariaLabel: `${b.text}`,
            text: `${b.text}`,
            href: b.href ?? '#',
            id: b.id ?? `${i}`
        };
    });
}

export function getExtraTaxonomy(
    searchPath: TaxonomyPage,
    query?: ParsedUrlQuery,
    resultsCount?: number
): Taxonomy {
    return {
        page: searchPath,
        section: 'find-agents',
        brand: 'zoopla',
        activity: 'agent_developer_directory',
        search_term_agent: query?.['company_name'] || '',
        search_term_location: query?.['q'] ?? null,
        search_result_count: resultsCount ? resultsCount : 0
    };
}

function getParamValue(param: string | string[] | undefined): string | null {
    if (Array.isArray(param)) return param[0];
    return param ?? null;
}

export const _getServerSideProps: GetServerSideProps<PageProps> = async ({
    req,
    params,
    query,
    resolvedUrl
}) => {
    let searchType: SearchType = SEARCH_TYPE.ALL;
    let searchPath: TaxonomyPage = '/find-agents/results/';

    if (params?.['search-path']?.[0] === 'estate-agents') {
        searchType = SEARCH_TYPE.ESTATE_AGENTS;
        searchPath = '/find-agents/estate-agents/results/';
    }
    if (params?.['search-path']?.[0] === 'letting-agents') {
        searchType = SEARCH_TYPE.LETTING_AGENTS;
        searchPath = '/find-agents/letting-agents/results/';
    }

    try {
        const url = 'https://' + req.headers.host + resolvedUrl;
        const { data } = await fetchGraphql<GetAgentsQuery>(GetAgentsDocument, {
            url
        });

        if (
            !data?.agents ||
            data.agents.__typename !== 'AgentsSearchConnection'
        ) {
            throw new Error();
        }

        const agentsEcommerce = {
            impressions: data.agents.results.map((agent, index) => ({
                id: agent.id,
                variant: agent.featured ? 'Featured' : 'Standard',
                position: index
            }))
        };

        return {
            props: {
                type: 'success',
                data: {
                    agents: data.agents,
                    breadcrumbs: apiToZdsBreadcrumbs(data.breadcrumbs),
                    isLoggedIn: !!data.user?.loggedIn
                },
                companyName: getParamValue(query?.['company_name']),
                searchType,
                initialLocation: resolvedUrl,
                taxonomy: getExtraTaxonomy(
                    searchPath,
                    query,
                    data.agents.total
                ),
                ecommerce: agentsEcommerce
            }
        };
    } catch {
        const errorProps: ErrorProps = {
            type: 'error',
            error: 'internal_server_error',
            initialLocation: resolvedUrl,
            taxonomy: getExtraTaxonomy(searchPath)
        };
        return {
            props: errorProps
        };
    }
};

export const getServerSideProps =
    withSSROptimizely.getServerSideProps(_getServerSideProps);
